import { createClient as createSupabaseClient, type SupabaseClient } from '@supabase/supabase-js'

import type { Database } from '#modules/database'
import { toast } from '@/lib/toast'

// Normalize a fetch() first-arg (string, URL, or Request) to its URL string.
function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

// Latched while the offline-auth toast is showing, so an auth retry burst shows
// one toast; cleared on the next successful auth fetch to re-arm per episode.
let offlineAuthToastShown = false

// The client's global.fetch. When offline, throw for auth requests and return a
// 400 for data requests.
export const offlineAwareFetch: typeof fetch = async (input, init) => {
  const isAuth = requestUrl(input).includes('/auth/')
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    // Throw for auth requests: a 400 on token refresh reads as fatal upstream and
    // signs the user out, but a thrown network error is retried with the session
    // kept.
    if (isAuth) {
      if (!offlineAuthToastShown) {
        offlineAuthToastShown = true
        toast.warning('Offline during a sign-in request — will retry when back online.')
      }
      throw new TypeError('Failed to fetch (offline)')
    }
    // Data requests: a PostgrestError-shaped 400 so callers fail cleanly offline.
    return new Response(JSON.stringify({
      message: 'OFFLINE',
      details: 'Device is offline',
      hint: '',
      code: 'PGRST000'
    }), {
      status: 400,
      headers: { 'Content-Type': 'application/json' },
    })
  }
  // A real auth fetch means connectivity is back; re-arm the toast.
  if (isAuth) offlineAuthToastShown = false
  return fetch(input, init)
}

// Singleton — one client for the entire app lifetime.
// Calling createClient() 79 times was creating 79 separate auth instances,
// GoTrue listeners, and connection pools → memory leak → browser crash.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _client: SupabaseClient<Database> | null = null

export function createClient() {
  if (!_client) {
    const url = import.meta.env.VITE_SUPABASE_URL
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY
    if (!url || !key) {
      console.error(
        'Supabase not configured.',
        'url=' + (url || 'missing') + ',',
        'key=' + (key ? 'set' : 'missing') + '.',
        'Check .env.local (local dev) or Vercel env vars (deployed).'
      )
      throw new Error('Service unavailable')
    }
    console.log('[client] supabase:', url)
    _client = createSupabaseClient<Database>(
      url,
      key,
      {
        global: {
          fetch: offlineAwareFetch,
        },
      }
    )
  }
  return _client
}
